import Image from 'next/image';
import { isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import type { GetStaticPropsContext } from 'next';
import { readFile } from 'node:fs/promises';
import path from 'node:path';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import styles from '@/styles/index.module.less';

type ReadmeSection = {
  title: string;
  content: string;
};

type ReadmeTab = {
  id: string;
  label: string;
  content: string;
};

const extractNodeText = (node: unknown): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map((item) => extractNodeText(item)).join('');
  }

  if (isValidElement(node)) {
    const props = node.props as { children?: unknown };
    return extractNodeText(props.children);
  }

  return '';
};

const parseReadmeSections = (content: string): ReadmeSection[] => {
  const normalized = content.replace(/\r\n/g, '\n').trim();
  const chunks = normalized.split(/^##\s+/m).filter(Boolean);

  return chunks
    .map((chunk) => {
      const [titleLine, ...rest] = chunk.split('\n');
      const title = titleLine?.trim();

      if (!title) {
        return null;
      }

      const sectionBody = rest.join('\n').trim();

      return {
        title,
        content: `## ${title}\n\n${sectionBody}`,
      };
    })
    .filter((item): item is ReadmeSection => item !== null);
};

const includesAnyKeyword = (title: string, keywords: readonly string[]) => {
  return keywords.some((keyword) => title.includes(keyword));
};

const faqs = [
  {
    q: '1. Loom 的配置文件放在哪里？',
    a: 'Loom 使用全局配置文件，默认路径为 macOS/Linux 的 ~/.loom/config.json 与 Windows 的 %APPDATA%/loom/config.json，也可通过 LOOM_CONFIG 指定自定义路径。配置完成后，AI 文档生成、API Schema 建模与 Mock 服务能力都会复用同一套设置。',
  },
  {
    q: '2. 首次使用需要做什么？',
    a: '首次执行 loom chat 时会进入引导流程，帮助你创建或更新全局配置；其中 apiKey 为必填项，其他参数可先使用默认值再按需调整。完成后即可开始用 AI 生成 API 文档、JSON Schema 与接口说明。',
  },
  {
    q: '3. 支持哪些模型或 Provider？',
    a: '当前 README 中明确支持通过 LLM provider 生成和更新文档，默认引导以 DeepSeek 为例，同时也可使用 OpenAI 等 provider（按配置切换）。你可以按团队偏好选择模型，用于 AI API 文档生成、Schema 设计与接口迭代。',
  },
  {
    q: '4. 可以一边聊天一边控制本地服务吗？',
    a: '可以。在 loom chat 中可直接使用 /mock、/view 及其 stop/restart 子命令控制 mock server 与文档查看器，减少来回切换。这样在编写 AI 生成的 API 文档时，也能同步验证接口返回与页面展示。',
  },
  {
    q: '5. Loom 支持自动升级吗？',
    a: '支持。启动时 Loom 会检查 npm 是否有新版本；你可以确认后自动升级，也可以手动执行 loom upgrade。保持版本更新有助于获得更稳定的 AI 文档生成体验与最新的 Schema 能力。',
  },
  {
    q: '6. 能否通过自然语言扫描现有代码接口？',
    a: '可以。你可以在聊天中使用 /scan <dir> 触发扫描，并通过 /scan resume、/scan reset 管理扫描进度。Loom 会把现有接口信息整理为更易维护的 API 文档、Schema 与实体定义，方便后续继续用 AI 迭代。',
  },
];

const heroPills = ['AI 文档生成', '多模型支持', 'Schema 联调'];
const commandHighlights = [
  { cmd: 'loom chat', desc: '自然语言生成 AI API 文档与 Schema' },
  { cmd: 'loom view', desc: '启动 API 文档与 Schema 查看器' },
  { cmd: 'loom mock', desc: '启动动态 Mock API 服务做联调' },
  { cmd: 'loom serve', desc: '文档、Schema 与 Mock 一体化运行' },
];

export default function Home({ readmeContent }: { readmeContent: string }) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const quickStartRef = useRef<HTMLElement | null>(null);
  const readmeTabsRef = useRef<HTMLDivElement | null>(null);
  const [copiedCode, setCopiedCode] = useState('');
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readmeTabs = useMemo<ReadmeTab[]>(() => {
    const sections = parseReadmeSections(readmeContent);

    const groups: Array<{ id: string; label: string; keywords: string[] }> = [
      { id: 'install', label: '安装与配置', keywords: ['安装', '配置'] },
      { id: 'start', label: '快速开始', keywords: ['快速开始'] },
      { id: 'usage', label: '命令与用法', keywords: ['使用指南', 'Usage Guide'] },
      { id: 'schema', label: 'Schema 与实体', keywords: ['JSON Schema', '实体 Schema', '实体建模'] },
      { id: 'community', label: '更新与支持', keywords: ['更新日志', '贡献', '许可证', '致谢', '支持'] },
    ];

    const tabs: ReadmeTab[] = [];

    groups.forEach((group) => {
      const matched = sections.filter((section) => {
        const matchedByTitle = includesAnyKeyword(section.title, group.keywords);

        return matchedByTitle;
      });

      if (!matched.length) {
        return;
      }

      tabs.push({
        id: group.id,
        label: group.label,
        content: matched.map((item) => item.content).join('\n\n'),
      });
    });

    return tabs;
  }, [readmeContent]);
  const [activeReadmeTab, setActiveReadmeTab] = useState('install');
  const [showFloatingTabs, setShowFloatingTabs] = useState(false);

  useEffect(() => {
    const handleScrollState = () => {
      const quickStartEl = quickStartRef.current;
      const readmeTabsEl = readmeTabsRef.current;

      if (!quickStartEl || !readmeTabsEl) {
        return;
      }

      const quickStartRect = quickStartEl.getBoundingClientRect();
      const tabsRect = readmeTabsEl.getBoundingClientRect();

      const sectionVisible = quickStartRect.bottom > 0 && quickStartRect.top < window.innerHeight;
      const tabsCovered = tabsRect.top <= 0;

      setShowFloatingTabs(sectionVisible && tabsCovered);
    };

    handleScrollState();
    window.addEventListener('scroll', handleScrollState, { passive: true });
    window.addEventListener('resize', handleScrollState);

    return () => {
      window.removeEventListener('scroll', handleScrollState);
      window.removeEventListener('resize', handleScrollState);
    };
  }, []);

  const activeReadmeContent = useMemo(() => {
    const currentTab = readmeTabs.find((item) => item.id === activeReadmeTab) ?? readmeTabs[0];
    return currentTab?.content ?? readmeContent;
  }, [activeReadmeTab, readmeContent, readmeTabs]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopyCode = async (text: string) => {
    if (!text) {
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopiedCode(text);

      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }

      copiedTimerRef.current = setTimeout(() => {
        setCopiedCode('');
      }, 1400);
    } catch {
      setCopiedCode('');
    }
  };

  const handleInstallClick = () => {
    document.getElementById('quick-start')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.page}>
      {/* Navbar */}
      <nav className={styles.navbar}>
        <div className={styles.navInner}>
          <div className={styles.logo}>
            <Image src="/images/icon-logo.png" alt="Loom" width={50} height={50} priority />
            <span className={styles.logoText}>loom</span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroPills} aria-label="核心能力标签">
          {heroPills.map((pill) => (
            <span key={pill} className={styles.heroPill}>
              {pill}
            </span>
          ))}
        </div>
        <h1 className={styles.heroTitle}>Loom — 面向 API Schema 的 AI 文档引擎</h1>
        <p className={styles.heroDesc}>
          <span className={styles.heroDescLine}>
            用自然语言驱动接口建模，自动生成结构化 JSON Schema、可复用实体、AI API 文档与 Mock API。
          </span>
          <br />
          内置 DeepSeek / OpenAI 等 provider 支持，一套命令即可完成 API 文档设计、联调、校验与迭代。
        </p>
        <button type="button" className={styles.ctaBtn} onClick={handleInstallClick}>
          如何安装
        </button>
      </section>

      <section className={styles.commandHighlights} aria-label="核心命令">
        {commandHighlights.map((item) => (
          <article key={item.cmd} className={styles.commandCard}>
            <code>{item.cmd}</code>
            <p>{item.desc}</p>
          </article>
        ))}
      </section>

      {/* Features */}
      <section className={styles.features}>
        <div className={styles.featureCard}>
          <div className={styles.featureIconWrap}>
            <Image src="/images/icon-api-schema.png" alt="API Schema" width={30} height={30} />
          </div>
          <h3>AI 驱动 Schema 生成</h3>
          <p>在 TUI 对话里直接描述接口意图，即可生成和更新 JSON Schema，快速完成接口定义、AI 文档生成与说明同步。</p>
        </div>
        <div className={styles.featureCard}>
          <div className={styles.featureIconWrap}>
            <Image src="/images/icon-entity-model.png" alt="Entity Model" width={30} height={30} />
          </div>
          <h3>实体优先的复用建模</h3>
          <p>将通用字段沉淀为实体并在多个端点复用，通过 x-entity-ref 保持请求/响应结构稳定一致，让 API 文档与数据模型更统一。</p>
        </div>
        <div className={styles.featureCard}>
          <div className={styles.featureIconWrap}>
            <Image src="/images/icon-mock-api.png" alt="Mock API" width={30} height={30} />
          </div>
          <h3>文档查看与 Mock 联动</h3>
          <p>内置 Web Viewer 与 Mock Server，支持聊天内服务控制，让前后端围绕同一份 API 文档与 Schema 并行开发和联调。</p>
        </div>
      </section>

      {/* Quick Start */}
      <section id="quick-start" className={styles.quickStart} ref={quickStartRef}>
        <h2 className={styles.sectionTitle}>快速开始</h2>
        <p className={styles.sectionSubtitle}>几步完成安装，立即开始用 AI 生成 API 文档、Schema 与 Mock 服务</p>
        {showFloatingTabs ? (
          <div className={styles.readmeTabsFloatingWrap} aria-hidden={false}>
            <div className={styles.readmeTabsFloating} role="tablist" aria-label="README 分组标签（浮动）">
              {readmeTabs.map((tab) => {
                const isActive = tab.id === activeReadmeTab;

                return (
                  <button
                    key={`floating-${tab.id}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`${styles.readmeTabBtn} ${isActive ? styles.readmeTabBtnActive : ''}`}
                    onClick={() => {
                      setActiveReadmeTab(tab.id);
                      handleInstallClick();
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className={styles.terminalBox}>
          <div className={styles.readmeTabs} role="tablist" aria-label="README 分组标签" ref={readmeTabsRef}>
            {readmeTabs.map((tab) => {
              const isActive = tab.id === activeReadmeTab;

              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`${styles.readmeTabBtn} ${isActive ? styles.readmeTabBtnActive : ''}`}
                  onClick={() => setActiveReadmeTab(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className={styles.markdownContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                pre({ children, ...props }) {
                  const codeText = extractNodeText(children).replace(/\n$/, '');
                  const isCopied = copiedCode === codeText;

                  return (
                    <div className={styles.codeBlockWrap}>
                      <button
                        type="button"
                        className={`${styles.codeCopyBtn} ${isCopied ? styles.codeCopyBtnCopied : ''}`}
                        onClick={() => handleCopyCode(codeText)}
                        aria-label={isCopied ? '已复制代码' : '复制代码'}
                        title={isCopied ? '已复制' : '复制代码'}
                      >
                        {isCopied ? (
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            aria-hidden="true"
                            className={styles.codeCopyBtnIcon}
                          >
                            <path d="M9.55 16.8 5.3 12.55l1.4-1.4 2.85 2.85 7.75-7.75 1.4 1.4Z" fill="currentColor" />
                          </svg>
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            aria-hidden="true"
                            className={styles.codeCopyBtnIcon}
                          >
                            <path
                              d="M8 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2Zm2 0v10h8V8Zm-4 2H4V4a2 2 0 0 1 2-2h8v2H6Z"
                              fill="currentColor"
                            />
                          </svg>
                        )}
                      </button>
                      <pre {...props}>{children}</pre>
                    </div>
                  );
                },
              }}
            >
              {activeReadmeContent}
            </ReactMarkdown>
          </div>
        </div>
      </section>

      {/* Demo Examples */}
      {/* <section className={styles.demos}>
        <div className={styles.demosHeader}>
          <h2 className={styles.sectionTitle}>示例演示</h2>
          <p className={styles.sectionSubtitle}>
            通过几个典型 API 场景，展示 Loom 如何从自然语言描述生成结构化 Schema，并自动产出接口文档与 Mock
            数据。所有示例均为高频开发需求，可直接应用于实际项目。
          </p>
        </div>
        <div className={styles.demoCards}>
          <div className={`${styles.demoCard} ${styles.demoCardSm}`}>
            <h3>用户登录接口生成</h3>
            <p>
              根据登录需求描述，自动生成包含请求参数、返回结构与错误响应的完整 Schema，并同步生成接口文档与 Mock
              数据，适用于大多数应用的认证模块开发。
            </p>
            <div className={styles.demoPreview}>
              <Image
                src="/images/1.gif"
                alt="用户登录接口生成示例"
                width={464}
                height={292}
                className={styles.demoPreviewImage}
                unoptimized
              />
            </div>
          </div>
          <div className={`${styles.demoCard} ${styles.demoCardLg}`}>
            <h3>用户信息查询接口</h3>
            <p>
              生成标准化的用户信息返回结构，并通过 Entity
              复用统一的数据模型，确保多个接口之间的数据字段保持一致，降低重复定义成本。
            </p>
            <div className={styles.demoPreview}>
              <Image
                src="/images/2.gif"
                alt="用户信息查询接口示例"
                width={722}
                height={352}
                className={styles.demoPreviewImage}
                unoptimized
              />
            </div>
          </div>
          <div className={`${styles.demoCard} ${styles.demoCardSmRight}`}>
            <h3>列表查询接口（分页结构）</h3>
            <p>
              自动生成包含分页参数与列表返回格式的 Schema
              结构，适用于商品列表、订单列表、文章列表等常见分页查询场景，减少手动设计通用数据结构的工作量。
            </p>
            <div className={styles.demoPreview}>
              <Image
                src="/images/3.gif"
                alt="列表查询接口示例"
                width={432}
                height={292}
                className={styles.demoPreviewImage}
                unoptimized
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* Testimonials */}
      {/* <section className={styles.testimonials}>
        <h2 className={styles.sectionTitle}>我们的客户这样评价</h2>
        <div className={styles.reviewList}>
          <div className={styles.reviewCard}>
            <Image src="/images/icon-quote-open.png" alt="" width={16} height={14} />
            <div className={styles.reviewer}>
              <Image
                src="/images/avatar-kevinzhang.png"
                alt="kevinzhang 头像"
                width={44}
                height={44}
                className={styles.avatar}
              />
              <span>kevinzhang</span>
            </div>
            <p>
              在设计新项目 API 时节省了很多时间。尤其是登录、用户信息这类标准接口，不需要再手动写
              schema，直接生成再微调即可。mock server 也很实用，前端可以提前联调。
            </p>
            <div className={styles.quoteEnd}>
              <Image src="/images/icon-quote-close.png" alt="" width={17} height={14} />
            </div>
          </div>
          <div className={`${styles.reviewCard} ${styles.reviewCardWide}`}>
            <Image src="/images/icon-quote-open.png" alt="" width={16} height={14} />
            <div className={styles.reviewer}>
              <Image
                src="/images/avatar-arthur-dev.png"
                alt="arthur_dev 头像"
                width={44}
                height={44}
                className={styles.avatar}
              />
              <span>arthur_dev</span>
            </div>
            <p>
              entity 的复用设计很有价值。以前多个接口之间的数据结构经常不一致，需要反复修改。现在可以统一维护，整体 API
              结构清晰很多。
            </p>
            <div className={styles.quoteEnd}>
              <Image src="/images/icon-quote-close.png" alt="" width={17} height={14} />
            </div>
          </div>
          <div className={styles.reviewCard}>
            <Image src="/images/icon-quote-open.png" alt="" width={16} height={14} />
            <div className={styles.reviewer}>
              <Image
                src="/images/avatar-linodev.png"
                alt="linodev 头像"
                width={44}
                height={44}
                className={styles.avatar}
              />
              <span>linodev</span>
            </div>
            <p>
              作为一个本地优先的工具体验很好，不需要复杂配置就能运行。生成的 schema 结构比较规范，也方便后续转换到其他
              API 文档工具。
            </p>
            <div className={styles.quoteEnd}>
              <Image src="/images/icon-quote-close.png" alt="" width={17} height={14} />
            </div>
          </div>
        </div>
      </section> */}

      {/* FAQ */}
      <section className={styles.faq}>
        <h2 className={styles.sectionTitle}>常见问题</h2>
        <p className={styles.sectionSubtitle}>围绕 AI 文档生成、配置、升级、扫描与本地 API 联调的高频问题</p>
        <div className={styles.faqList}>
          {faqs.map((item, i) => {
            const isOpen = openFaq === i;

            return (
              <div key={i} className={styles.faqItem}>
                <button
                  type="button"
                  className={styles.faqQuestion}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${i}`}
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                >
                  <span>{item.q}</span>
                  <Image
                    src={isOpen ? '/images/icon-faq-collapse.png' : '/images/icon-faq-expand.png'}
                    alt={isOpen ? '收起' : '展开'}
                    width={50}
                    height={33}
                    className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`}
                  />
                </button>
                <div
                  id={`faq-answer-${i}`}
                  className={`${styles.faqAnswerWrap} ${isOpen ? styles.faqAnswerWrapOpen : ''}`}
                >
                  {item.a && <p className={styles.faqAnswer}>{item.a}</p>}
                </div>
                <div className={styles.faqDivider} />
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <p>
          Loom 文档首页 · GitHub：
          <a href="https://github.com/husu/loom/" target="_blank" rel="noreferrer">
            https://github.com/husu/loom/
          </a>
        </p>
      </footer>
    </div>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const activeLocale = locale ?? 'zh';
  const isChinese = activeLocale === 'zh';
  const readmePath = path.join(process.cwd(), 'public', isChinese ? 'README.md' : 'README.en.md');
  const readmeContent = await readFile(readmePath, 'utf-8');
  const seoByLocale = {
    zh: {
      title: 'Loom - 用自然语言生成 API Schema 与文档的 AI Agent',
      description:
        'Loom 是面向开发者的开源 AI Agent，可从自然语言生成 JSON Schema API、可复用数据模型、AI API 文档、接口说明与 Mock 服务，帮助团队更快完成 API 设计、联调与交付。',
      keywords:
        'AI API 生成器, AI 文档生成, API 文档生成, API 文档工具, API Schema 生成, JSON Schema 生成器, 接口文档, 接口说明生成, Mock Server, 接口设计, 自然语言生成接口, 开发者 AI 工具, 后端开发工具, Schema 驱动开发',
      ogLocale: 'zh_CN',
    },
    en: {
      title: 'Loom - AI Agent for API Schema Generation and Documentation',
      description:
        'Loom is an open-source AI agent for developers that turns natural language into JSON Schema APIs, reusable data models, documentation, and mock servers.',
      keywords:
        'AI API generator, API schema generator, JSON Schema generator, API documentation tool, mock server generator, schema driven API design, developer AI tools',
      ogLocale: 'en_US',
    },
    de: {
      title: 'Loom - AI Agent for API Schema Generation and Documentation',
      description:
        'Loom is an open-source AI agent for developers that turns natural language into JSON Schema APIs, reusable data models, documentation, and mock servers.',
      keywords:
        'AI API generator, API schema generator, JSON Schema generator, API documentation tool, mock server generator, schema driven API design, developer AI tools',
      ogLocale: 'de_DE',
    },
  } as const;

  return {
    props: {
      messages: (await import(`@/locales/${activeLocale}.json`)).default,
      readmeContent,
      seo: seoByLocale[activeLocale as keyof typeof seoByLocale] ?? seoByLocale.zh,
    },
  };
}
